import React, { useContext, useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory, useParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../contexts/AuthContext';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import LinearProgress from '@material-ui/core/LinearProgress';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Chip from '@material-ui/core/Chip';
import Tooltip from '@material-ui/core/Tooltip';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import VisibilityIcon from '@material-ui/icons/Visibility';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import GetAppIcon from '@material-ui/icons/GetApp';
import SearchIcon from '@material-ui/icons/Search';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import CloseIcon from '@material-ui/icons/Close';
import EmailIcon from '@material-ui/icons/Email';
import MouseIcon from '@material-ui/icons/Mouse';
import UnsubscribeIcon from '@material-ui/icons/Unsubscribe';
import ErrorIcon from '@material-ui/icons/Error';
import WarningIcon from '@material-ui/icons/Warning';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import AssessmentIcon from '@material-ui/icons/Assessment';
import PollIcon from '@material-ui/icons/Poll';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  backButton: {
    marginRight: theme.spacing(1),
  },
  statsGrid: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    '& > *': {
      flex: '1 1 0',
      minWidth: '180px',
    },
  },
  statsCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(1.5),
  },
  statsIcon: {
    fontSize: 28,
    marginBottom: theme.spacing(0.5),
  },
  statsValue: {
    fontSize: '1.5rem',
    fontWeight: 600,
    marginBottom: theme.spacing(0.25),
    lineHeight: 1.2,
  },
  statsLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    lineHeight: 1.3,
  },
  campaignInfo: {
    marginBottom: theme.spacing(3),
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  searchField: {
    marginBottom: theme.spacing(2),
    width: '100%',
    maxWidth: 400,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  table: {
    '& .MuiTableCell-head': {
      fontWeight: 600,
      backgroundColor: theme.palette.grey[100],
    },
  },
  previewDialog: {
    '& .MuiDialog-paper': {
      maxWidth: '800px',
      width: '100%',
    },
  },
  previewContent: {
    minHeight: '400px',
  },
}));

const CampaignDetails = () => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();
  const { user, token } = useContext(AuthContext);
  const [campaign, setCampaign] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [orderBy, setOrderBy] = useState('email');
  const [order, setOrder] = useState('asc');
  const [stats, setStats] = useState({
    totalSent: 0,
    totalOpens: 0,
    uniqueOpens: 0,
    totalClicks: 0,
    uniqueClicks: 0,
    unsubscribes: 0,
    openRate: 0,
    clickRate: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [surveyFunnel, setSurveyFunnel] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/';

  useEffect(() => {
    fetchCampaignDetails();
  }, [id]);

  useEffect(() => {
    if (campaign && campaign.campaign_type === 'survey') {
      fetchSurveyFunnel();
    }
  }, [campaign]);

  const fetchCampaignDetails = async () => {
    setIsLoading(true);
    try {
      // Fetch campaign info
      const campaignResponse = await axios.get(`${API_URL}email-campaigns/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCampaign(campaignResponse.data.campaign);

      // Fetch recipient details with stats
      const recipientsResponse = await axios.get(`${API_URL}email-campaigns/${id}/recipients`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRecipients(recipientsResponse.data.recipients || []);

      // Calculate aggregate stats
      const recipientData = recipientsResponse.data.recipients || [];
      const totalSent = recipientData.length;
      const uniqueOpens = recipientData.filter(r => r.opened).length;
      const totalOpens = recipientData.reduce((sum, r) => sum + (r.open_count || 0), 0);
      const uniqueClicks = recipientData.filter(r => r.clicked).length;
      const totalClicks = recipientData.reduce((sum, r) => sum + (r.click_count || 0), 0);
      const unsubscribes = recipientData.filter(r => r.unsubscribed).length;
      const hardBounces = recipientData.filter(r => r.bounce_type === 'hard').length;
      const softBounces = recipientData.filter(r => r.bounce_type === 'soft').length;
      const totalBounces = hardBounces + softBounces;

      setStats({
        totalSent,
        totalOpens,
        uniqueOpens,
        totalClicks,
        uniqueClicks,
        unsubscribes,
        hardBounces,
        softBounces,
        totalBounces,
        openRate: totalSent > 0 ? ((uniqueOpens / totalSent) * 100).toFixed(1) : 0,
        clickRate: totalSent > 0 ? ((uniqueClicks / totalSent) * 100).toFixed(1) : 0,
        bounceRate: totalSent > 0 ? ((totalBounces / totalSent) * 100).toFixed(1) : 0,
      });
    } catch (error) {
      console.error('Error fetching campaign details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSurveyFunnel = async () => {
    setFunnelLoading(true);
    try {
      const response = await axios.get(`${API_URL}email-campaigns/${id}/survey-funnel`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSurveyFunnel(response.data.funnel);
    } catch (error) {
      console.error('Error fetching survey funnel:', error);
      setSurveyFunnel(null);
    } finally {
      setFunnelLoading(false);
    }
  };

  const handlePreview = () => {
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
  };

  const handleDuplicate = () => {
    history.push('/email-campaigns/create', { duplicateFrom: campaign });
  };

  const handleExport = async () => {
    // TODO: Implement Excel export
    console.log('Export to Excel:', id);
  };

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = (a, b, orderBy) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];

    // Handle null/undefined values
    if (!aValue && aValue !== 0) aValue = '';
    if (!bValue && bValue !== 0) bValue = '';

    // Convert to lowercase for string comparison
    if (typeof aValue === 'string') aValue = aValue.toLowerCase();
    if (typeof bValue === 'string') bValue = bValue.toLowerCase();

    if (bValue < aValue) return -1;
    if (bValue > aValue) return 1;
    return 0;
  };

  const filterRecipients = () => {
    if (!searchTerm) return recipients;

    return recipients.filter((recipient) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        recipient.email?.toLowerCase().includes(searchLower) ||
        recipient.first_name?.toLowerCase().includes(searchLower) ||
        recipient.last_name?.toLowerCase().includes(searchLower) ||
        (recipient.unsubscribed ? 'yes' : 'no').includes(searchLower)
      );
    });
  };

  const sortedAndFilteredRecipients = () => {
    const filtered = filterRecipients();
    return filtered.sort(getComparator(order, orderBy));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (isLoading && !campaign) {
    return (
      <div className={classes.root}>
        <LinearProgress />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className={classes.root}>
        <Typography variant="h6">Campaign not found</Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Box className={classes.header}>
        <IconButton
          className={classes.backButton}
          onClick={() => history.push('/email-campaigns')}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">{campaign.name}</Typography>
        <Chip
          label={campaign.status}
          color={campaign.status === 'sent' ? 'secondary' : 'default'}
        />
      </Box>

      {isLoading && <LinearProgress />}

      {/* Campaign Info */}
      <Paper className={classes.campaignInfo}>
        <Box p={3}>
          <Typography variant="h6" gutterBottom>Campaign Information</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="textSecondary">Subject:</Typography>
              <Typography variant="body1">{campaign.subject}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="textSecondary">From:</Typography>
              <Typography variant="body1">
                {campaign.from_name} &lt;{campaign.from_email}&gt;
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="textSecondary">Created:</Typography>
              <Typography variant="body1">{formatDate(campaign.created_at)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="textSecondary">Sent:</Typography>
              <Typography variant="body1">{formatDate(campaign.sent_at)}</Typography>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Action Buttons */}
      <Box className={classes.actionButtons}>
        <Button
          variant="outlined"
          startIcon={<VisibilityIcon />}
          onClick={handlePreview}
        >
          Preview
        </Button>
        <Button
          variant="outlined"
          startIcon={<FileCopyIcon />}
          onClick={handleDuplicate}
        >
          Duplicate
        </Button>
        <Button
          variant="outlined"
          startIcon={<GetAppIcon />}
          onClick={handleExport}
        >
          Export to Excel
        </Button>
      </Box>

      {/* Stats Cards */}
      <Box className={classes.statsGrid}>
        <Card className={classes.statsCard}>
          <CardContent align="center" style={{ padding: 12, paddingBottom: 12 }}>
            <EmailIcon color="primary" className={classes.statsIcon} />
            <Typography className={classes.statsValue}>{stats.totalSent}</Typography>
            <Typography className={classes.statsLabel}>Sent</Typography>
          </CardContent>
        </Card>
        <Card className={classes.statsCard}>
          <CardContent align="center" style={{ padding: 12, paddingBottom: 12 }}>
            <CheckCircleIcon style={{ color: '#4caf50' }} className={classes.statsIcon} />
            <Typography className={classes.statsValue}>
              {stats.uniqueOpens}
            </Typography>
            <Typography className={classes.statsLabel}>
              Opens ({stats.openRate}%)
            </Typography>
          </CardContent>
        </Card>
        <Card className={classes.statsCard}>
          <CardContent align="center" style={{ padding: 12, paddingBottom: 12 }}>
            <MouseIcon style={{ color: '#2196f3' }} className={classes.statsIcon} />
            <Typography className={classes.statsValue}>
              {stats.uniqueClicks}
            </Typography>
            <Typography className={classes.statsLabel}>
              Clicks ({stats.clickRate}%)
            </Typography>
          </CardContent>
        </Card>
        <Card className={classes.statsCard}>
          <CardContent align="center" style={{ padding: 12, paddingBottom: 12 }}>
            <ErrorIcon style={{ color: '#ff9800' }} className={classes.statsIcon} />
            <Typography className={classes.statsValue}>
              {stats.totalBounces}
            </Typography>
            <Typography className={classes.statsLabel}>
              Bounces ({stats.bounceRate}%)
            </Typography>
          </CardContent>
        </Card>
        <Card className={classes.statsCard}>
          <CardContent align="center" style={{ padding: 12, paddingBottom: 12 }}>
            <UnsubscribeIcon style={{ color: '#f44336' }} className={classes.statsIcon} />
            <Typography className={classes.statsValue}>{stats.unsubscribes}</Typography>
            <Typography className={classes.statsLabel}>Unsubs</Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Survey Funnel (Only for survey campaigns) */}
      {campaign.campaign_type === 'survey' && surveyFunnel && (
        <Paper style={{ marginBottom: 24, padding: 16 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <AssessmentIcon style={{ marginRight: 8, color: '#673ab7' }} />
            <Typography variant="h6">Survey Conversion Funnel</Typography>
          </Box>
          
          {funnelLoading ? (
            <LinearProgress />
          ) : (
            <>
              {/* Funnel Metric Cards - Compact Grid */}
              <Box className={classes.statsGrid} style={{ marginBottom: 16 }}>
                <Card className={classes.statsCard}>
                  <CardContent align="center" style={{ padding: 12, paddingBottom: 12 }}>
                    <EmailIcon color="primary" className={classes.statsIcon} />
                    <Typography className={classes.statsValue}>{surveyFunnel.emails_sent}</Typography>
                    <Typography className={classes.statsLabel}>Emails Sent</Typography>
                  </CardContent>
                </Card>
                <Card className={classes.statsCard}>
                  <CardContent align="center" style={{ padding: 12, paddingBottom: 12 }}>
                    <CheckCircleIcon style={{ color: '#4caf50' }} className={classes.statsIcon} />
                    <Typography className={classes.statsValue}>{surveyFunnel.emails_opened}</Typography>
                    <Typography className={classes.statsLabel}>
                      Opened ({surveyFunnel.email_open_rate}%)
                    </Typography>
                  </CardContent>
                </Card>
                <Card className={classes.statsCard}>
                  <CardContent align="center" style={{ padding: 12, paddingBottom: 12 }}>
                    <MouseIcon style={{ color: '#2196f3' }} className={classes.statsIcon} />
                    <Typography className={classes.statsValue}>{surveyFunnel.links_clicked}</Typography>
                    <Typography className={classes.statsLabel}>
                      Clicked ({surveyFunnel.click_through_rate}%)
                    </Typography>
                  </CardContent>
                </Card>
                <Card className={classes.statsCard}>
                  <CardContent align="center" style={{ padding: 12, paddingBottom: 12 }}>
                    <PollIcon style={{ color: '#9c27b0' }} className={classes.statsIcon} />
                    <Typography className={classes.statsValue}>{surveyFunnel.survey_started}</Typography>
                    <Typography className={classes.statsLabel}>
                      Started ({surveyFunnel.email_to_survey_start_rate}%)
                    </Typography>
                  </CardContent>
                </Card>
                <Card className={classes.statsCard}>
                  <CardContent align="center" style={{ padding: 12, paddingBottom: 12 }}>
                    <TrendingUpIcon style={{ color: '#673ab7' }} className={classes.statsIcon} />
                    <Typography className={classes.statsValue}>{surveyFunnel.survey_completed}</Typography>
                    <Typography className={classes.statsLabel}>
                      Completed ({surveyFunnel.overall_conversion_rate}%)
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              {/* Compact Funnel Visualization - Mobile-friendly */}
              <Box mt={2} p={2} style={{ backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Conversion Flow
                </Typography>
                <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
                  <Chip label={`${surveyFunnel.emails_sent} Sent`} size="small" />
                  <Typography variant="body2">→</Typography>
                  <Chip label={`${surveyFunnel.emails_opened} Opened`} size="small" color="primary" />
                  <Typography variant="body2">→</Typography>
                  <Chip label={`${surveyFunnel.links_clicked} Clicked`} size="small" style={{ backgroundColor: '#2196f3', color: 'white' }} />
                  <Typography variant="body2">→</Typography>
                  <Chip label={`${surveyFunnel.survey_started} Started Survey`} size="small" style={{ backgroundColor: '#9c27b0', color: 'white' }} />
                  <Typography variant="body2">→</Typography>
                  <Chip label={`${surveyFunnel.survey_completed} Completed`} size="small" style={{ backgroundColor: '#4caf50', color: 'white' }} />
                </Box>
              </Box>
            </>
          )}
        </Paper>
      )}

      {/* Recipients Table */}
      <Paper className={classes.tableContainer}>
        <Box p={2}>
          <Typography variant="h6" gutterBottom>Recipients</Typography>
          <TextField
            className={classes.searchField}
            variant="outlined"
            size="small"
            placeholder="Search recipients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <TableContainer>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'email'}
                    direction={orderBy === 'email' ? order : 'asc'}
                    onClick={() => handleSort('email')}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'first_name'}
                    direction={orderBy === 'first_name' ? order : 'asc'}
                    onClick={() => handleSort('first_name')}
                  >
                    First Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'last_name'}
                    direction={orderBy === 'last_name' ? order : 'asc'}
                    onClick={() => handleSort('last_name')}
                  >
                    Last Name
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'opened'}
                    direction={orderBy === 'opened' ? order : 'asc'}
                    onClick={() => handleSort('opened')}
                  >
                    Opened
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'open_count'}
                    direction={orderBy === 'open_count' ? order : 'asc'}
                    onClick={() => handleSort('open_count')}
                  >
                    Opens
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'clicked'}
                    direction={orderBy === 'clicked' ? order : 'asc'}
                    onClick={() => handleSort('clicked')}
                  >
                    Clicked
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'click_count'}
                    direction={orderBy === 'click_count' ? order : 'asc'}
                    onClick={() => handleSort('click_count')}
                  >
                    Clicks
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={orderBy === 'unsubscribed'}
                    direction={orderBy === 'unsubscribed' ? order : 'asc'}
                    onClick={() => handleSort('unsubscribed')}
                  >
                    Unsubscribed
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedAndFilteredRecipients().map((recipient, index) => (
                <TableRow key={index}>
                  <TableCell>{recipient.email}</TableCell>
                  <TableCell>{recipient.first_name || '-'}</TableCell>
                  <TableCell>{recipient.last_name || '-'}</TableCell>
                  <TableCell align="center">
                    {recipient.opened ? (
                      <CheckCircleIcon style={{ color: '#4caf50' }} fontSize="small" />
                    ) : (
                      <CancelIcon color="disabled" fontSize="small" />
                    )}
                  </TableCell>
                  <TableCell align="center">{recipient.open_count || 0}</TableCell>
                  <TableCell align="center">
                    {recipient.clicked ? (
                      <CheckCircleIcon style={{ color: '#2196f3' }} fontSize="small" />
                    ) : (
                      <CancelIcon color="disabled" fontSize="small" />
                    )}
                  </TableCell>
                  <TableCell align="center">{recipient.click_count || 0}</TableCell>
                  <TableCell align="center">
                    {recipient.unsubscribed ? (
                      <Chip label="Yes" size="small" color="secondary" />
                    ) : (
                      <Chip label="No" size="small" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {sortedAndFilteredRecipients().length === 0 && !isLoading && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="textSecondary">
                      {searchTerm
                        ? 'No recipients found matching your search.'
                        : 'No recipients found for this campaign.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Bounced Emails Section */}
      {stats.totalBounces > 0 && (
        <Paper className={classes.tableContainer} style={{ marginTop: 24 }}>
          <Box p={2}>
            <Box display="flex" alignItems="center" mb={2}>
              <ErrorIcon style={{ color: '#ff9800', marginRight: 8 }} />
              <Typography variant="h6">Bounced Emails ({stats.totalBounces})</Typography>
            </Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              These emails failed to deliver. Hard bounces are permanent failures (invalid email), 
              soft bounces are temporary (mailbox full, server issues).
            </Typography>
            <TableContainer>
              <Table className={classes.table} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell align="center">Bounce Type</TableCell>
                    <TableCell>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recipients
                    .filter(r => r.bounce_type)
                    .map((recipient, index) => (
                      <TableRow key={index}>
                        <TableCell>{recipient.email}</TableCell>
                        <TableCell>
                          {[recipient.first_name, recipient.last_name].filter(Boolean).join(' ') || '-'}
                        </TableCell>
                        <TableCell align="center">
                          {recipient.bounce_type === 'hard' ? (
                            <Chip 
                              icon={<ErrorIcon />}
                              label="Hard Bounce" 
                              size="small" 
                              style={{ backgroundColor: '#f44336', color: 'white' }}
                            />
                          ) : (
                            <Chip 
                              icon={<WarningIcon />}
                              label="Soft Bounce" 
                              size="small" 
                              style={{ backgroundColor: '#ff9800', color: 'white' }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={recipient.bounce_reason || 'No details available'}>
                            <Typography variant="body2" noWrap style={{ maxWidth: 400 }}>
                              {recipient.bounce_reason || 'No details available'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>
      )}

      {/* Preview Modal */}
      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        className={classes.previewDialog}
        maxWidth="md"
        fullWidth
        disableEnforceFocus={true}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Campaign Preview</Typography>
            <IconButton onClick={handleClosePreview} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent className={classes.previewContent}>
          {campaign && (
            <>
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary">Subject:</Typography>
                <Typography variant="body1">{campaign.subject}</Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary">From:</Typography>
                <Typography variant="body1">
                  {campaign.from_name} &lt;{campaign.from_email}&gt;
                </Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Email Content:
                </Typography>
                <Paper elevation={1} style={{ padding: '16px', maxHeight: '500px', overflow: 'auto' }}>
                  <div dangerouslySetInnerHTML={{ __html: campaign.body }} />
                </Paper>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CampaignDetails;
